import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Controller,
  ControllerRenderProps,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import { RefreshCwIcon } from 'lucide-react';

import { Label } from 'components/Label';
import { BaseOptionType, Select } from 'components/Select';
import { Input } from 'components/input/Input';
import { TemperatureUnitSelector } from 'components/input/TemperatureUnitSelector';
import { Flex, Grid } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { ServiceOption } from 'lib/api/getServiceOptions';
import { TransportType } from 'lib/api/getTransportTypes';
import {
  EQUIPMENT_PLACEHOLDER,
  EQUIPMENT_REQUIRED_MESSAGE,
  validateEquipmentRequired,
} from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import { Commodity, NormalizedLoad } from 'types/Load';
import { Maybe, MaybeUndef, Undef } from 'types/UtilityTypes';
import { TemperatureUnit } from 'types/enums/TemperatureUnit';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { validateRequiredSelectMembership } from 'utils/formValidation';
import { devDisableRequiredFields } from 'utils/loadInfoAndBuilding';

import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';
import {
  ALL_THREEG_MODES,
  MOHAWK_PROD_MODES,
  MOHAWK_PROD_TMSID,
} from './constants';
import {
  calculatePalletsAndPiecesFromCommodities,
  hasCommodityCountData,
} from './utils/threeGUtils';

export function SpecificationsForm({
  formMethods,
  transportTypes,
  isLoadingTransportTypes,
  handleRefreshTransportTypes,
  serviceOptions,
  isLoadingServiceOptions,
  handleRefreshServiceOptions,
  tmsID,
  isCreateMode = false,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
  transportTypes: Maybe<TransportType[]>;
  isLoadingTransportTypes: boolean;
  handleRefreshTransportTypes: () => void;
  serviceOptions: Maybe<ServiceOption[]>;
  isLoadingServiceOptions: boolean;
  handleRefreshServiceOptions: () => void;
  tmsID?: number;
  isCreateMode?: boolean;
}) {
  const {
    control,
    formState: { errors },
    setValue,
    watch,
    getValues,
    trigger,
  } = formMethods;

  // Use limited modes for Mohawk Prod, otherwise use full list
  const modes =
    tmsID === MOHAWK_PROD_TMSID ? MOHAWK_PROD_MODES : ALL_THREEG_MODES;

  const {
    currentState: { clickedSuggestion },
  } = useContext(SidebarStateContext);

  const [tempUnit, setTempUnit] = useState<TemperatureUnit>(
    TemperatureUnit.Fahrenheit
  );
  const [tempDisplayValue, setTempDisplayValue] = useState<string>('');
  const [isTempInputFocused, setIsTempInputFocused] = useState<boolean>(false);

  const formatDisplayTemp = useCallback(
    (fahrenheit: MaybeUndef<number>) => {
      if (fahrenheit === undefined || fahrenheit === null) {
        return '';
      }
      if (tempUnit === 'F') {
        return fahrenheit.toString();
      }
      return (((fahrenheit - 32) * 5) / 9).toFixed(1);
    },
    [tempUnit]
  );

  const parseInputToFahrenheit = (value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return null;
    if (tempUnit === 'F') return numValue;
    return (numValue * 9) / 5 + 32;
  };

  useEffect(() => {
    if (!isCreateMode || isLoadingTransportTypes) {
      return;
    }
    const current = String(
      getValues('specifications.transportType') ?? ''
    ).trim();
    if (!current) {
      return;
    }
    void trigger('specifications.transportType');
  }, [
    getValues,
    isCreateMode,
    isLoadingTransportTypes,
    transportTypes,
    trigger,
  ]);

  // When the unit changes, we need to update the form value to reflect the new unit.
  useEffect(() => {
    const currentFahrenheit = getValues('specifications.minTempFahrenheit');
    setValue('specifications.minTempFahrenheit', currentFahrenheit, {
      shouldDirty: true,
    });
  }, [tempUnit, getValues, setValue]);

  const watchedTransportType = useWatch({
    control,
    name: 'specifications.transportType',
  });

  const isRefrigerated =
    watchedTransportType?.toLowerCase().includes('reefer') ||
    watchedTransportType?.toLowerCase().includes('refrigerated');

  // Watch for the min temp to be set
  const watchedMinTemp = watch('specifications.minTempFahrenheit');

  useEffect(() => {
    if (!isTempInputFocused) {
      setTempDisplayValue(formatDisplayTemp(watchedMinTemp));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedMinTemp, isTempInputFocused, formatDisplayTemp]);

  useEffect(() => {
    if (
      clickedSuggestion &&
      clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      return;
    }

    setValue('specifications.minTempFahrenheit', null, { shouldDirty: true });
    setValue('specifications.maxTempFahrenheit', null, { shouldDirty: true });
  }, [setValue, watchedTransportType, clickedSuggestion]);

  // When minTempFahrenheit changes, update maxTempFahrenheit to match
  useEffect(() => {
    if (watchedMinTemp !== undefined) {
      setValue('specifications.maxTempFahrenheit', watchedMinTemp);
    }
  }, [watchedMinTemp, setValue]);

  // Keep totalInPalletCount in sync with totalOutPalletCount (extractor sets both to same value)
  const watchedOutPallets = watch('specifications.totalOutPalletCount');
  useEffect(() => {
    if (
      typeof watchedOutPallets === 'number' &&
      Number.isFinite(watchedOutPallets) &&
      watchedOutPallets >= 0
    ) {
      setValue('specifications.totalInPalletCount', watchedOutPallets, {
        shouldDirty: true,
      });
    }
  }, [watchedOutPallets, setValue]);

  // Count from commodity totals when available; otherwise leave Pallets/Pieces as-is (current form values)
  const watchedCommodities = useWatch({ control, name: 'commodities' });

  const syncPalletsAndPiecesFromCommodities = useCallback(() => {
    const commodities = (watchedCommodities || []) as Commodity[];
    const hasCountData = commodities.some(hasCommodityCountData);

    if (hasCountData) {
      const { totalPallets, totalPieces } =
        calculatePalletsAndPiecesFromCommodities(commodities);
      setValue('specifications.totalOutPalletCount', totalPallets, {
        shouldDirty: true,
      });
      setValue('specifications.totalInPalletCount', totalPallets, {
        shouldDirty: true,
      });
      setValue('specifications.totalPieces.val', totalPieces, {
        shouldDirty: true,
      });
      return;
    }

    // No items or no count data: leave Pallets/Pieces unchanged so we don't overwrite
    // user edits or async-restored saved state (no ref, no setValue).
  }, [watchedCommodities, setValue]);
  useEffect(() => {
    syncPalletsAndPiecesFromCommodities();
  }, [syncPalletsAndPiecesFromCommodities]);

  return (
    <Grid cols='4' gap='sm' className='mx-0 w-full'>
      {/* Only Truckloads supported rn, not LTL */}
      <div className='col-span-4'>
        <Label hideAIHint={true} name={'mode'} required={true}>
          Mode
        </Label>
        {/* <div className='flex flex-row w-full items-center gap-2'> */}
        <Controller
          name='mode'
          control={control}
          rules={{ required: devDisableRequiredFields ? false : 'Required' }}
          render={({
            field,
          }: {
            field: ControllerRenderProps<NormalizedLoad, 'mode'>;
          }) => (
            <div className='text-neutral-500'>
              <Select
                showSearch
                name={field.name}
                disabled={false} // Only Truckloads supported rn, not LTL
                className='text-neutral-500'
                placeholder={'Choose'}
                filterOption={(input: string, option: Undef<BaseOptionType>) =>
                  (
                    option?.label?.toString().toLocaleLowerCase() ?? ''
                  ).includes(input.toLocaleLowerCase())
                }
                filterSort={(
                  optionA: BaseOptionType,
                  optionB: BaseOptionType
                ) =>
                  (optionA?.label?.toString() ?? '')
                    .toLowerCase()
                    .localeCompare(
                      (optionB?.label?.toString() ?? '').toLowerCase()
                    )
                }
                onChange={field.onChange}
                value={field.value}
                options={modes?.map((mode) => ({
                  value: mode,
                  label: mode,
                }))}
              />
            </div>
          )}
        />
        {/* </div> */}
        <ErrorMessage
          errors={errors}
          name={'mode'}
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>
      <div className='col-span-4'>
        <Flex direction='row' gap='xs' className='items-center'>
          <Label
            name={'specifications.serviceType'}
            // We always default this value so don't show AI hint unless there's a suggestion
            hideAIHint={
              !clickedSuggestion ||
              clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
            }
            required={true}
            className='flex-1'
          >
            Service Options
          </Label>
          {handleRefreshServiceOptions && (
            <button
              title='Refresh service options'
              onClick={handleRefreshServiceOptions}
              type='button'
              className='shrink-0 py-1 px-1 hover:text-info-600 rounded-full transition-colors cursor-pointer'
              disabled={isLoadingServiceOptions}
            >
              {isLoadingServiceOptions ? (
                <ButtonLoader className='mr-0' />
              ) : (
                <RefreshCwIcon className='h-4 w-4' />
              )}
            </button>
          )}
        </Flex>
        <Controller
          name='specifications.serviceType'
          control={control}
          rules={{ required: devDisableRequiredFields ? false : 'Required' }}
          render={({
            field,
          }: {
            field: ControllerRenderProps<
              NormalizedLoad,
              'specifications.serviceType'
            >;
          }) => (
            <div className='text-neutral-500'>
              <Select
                showSearch
                name={field.name}
                disabled={false}
                className='text-neutral-500'
                placeholder={'Choose'}
                filterOption={(input: string, option: Undef<BaseOptionType>) =>
                  (
                    option?.label?.toString().toLocaleLowerCase() ?? ''
                  ).includes(input.toLocaleLowerCase())
                }
                filterSort={(
                  optionA: BaseOptionType,
                  optionB: BaseOptionType
                ) =>
                  (optionA?.label?.toString() ?? '')
                    .toLowerCase()
                    .localeCompare(
                      (optionB?.label?.toString() ?? '').toLowerCase()
                    )
                }
                onChange={field.onChange}
                value={field.value || undefined}
                options={(serviceOptions || [])
                  .filter((option) => option && (option.name || option.id))
                  .map((option) => ({
                    value: option.id,
                    label: option.name || option.id || '',
                  }))}
              />
            </div>
          )}
        />
        <ErrorMessage
          errors={errors}
          name={'specifications.serviceType'}
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>
      <div className='col-span-4'>
        <Flex direction='row' gap='xs' className='items-center'>
          <Label
            name={'specifications.transportType'}
            // We always default this value so don't show AI hint unless there's a suggestion
            hideAIHint={
              !clickedSuggestion ||
              clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
            }
            required={true}
            className='flex-1'
          >
            Equipment
          </Label>
          {handleRefreshTransportTypes && (
            <button
              title='Refresh transport types'
              onClick={handleRefreshTransportTypes}
              type='button'
              className='shrink-0 py-1 px-1 hover:text-info-600 rounded-full transition-colors cursor-pointer'
              disabled={isLoadingTransportTypes}
            >
              {isLoadingTransportTypes ? (
                <ButtonLoader className='mr-0' />
              ) : (
                <RefreshCwIcon className='h-4 w-4' />
              )}
            </button>
          )}
        </Flex>
        <Controller
          name='specifications.transportType'
          control={control}
          rules={{
            validate: (value) => {
              if (!isCreateMode) {
                return validateEquipmentRequired(value);
              }
              return validateRequiredSelectMembership(
                value,
                (transportTypes || [])
                  .map((type) => String(type.value || type.label || '').trim())
                  .filter(Boolean),
                {
                  isLoading: isLoadingTransportTypes,
                  message: EQUIPMENT_REQUIRED_MESSAGE,
                }
              );
            },
          }}
          render={({
            field,
          }: {
            field: ControllerRenderProps<
              NormalizedLoad,
              'specifications.transportType'
            >;
          }) => {
            const optionValues = new Set(
              (transportTypes || [])
                .map((type) => String(type.value || type.label || '').trim())
                .filter(Boolean)
            );
            const selected = String(field.value ?? '').trim();
            let selectValue: Undef<string>;
            if (selected) {
              if (!isCreateMode || optionValues.has(selected)) {
                selectValue = selected;
              }
            }
            return (
              <Select
                showSearch
                name={field.name}
                className='text-neutral-500'
                placeholder={EQUIPMENT_PLACEHOLDER}
                filterOption={(input: string, option: Undef<BaseOptionType>) =>
                  (
                    option?.label?.toString().toLocaleLowerCase() ?? ''
                  ).includes(input.toLocaleLowerCase())
                }
                filterSort={(
                  optionA: BaseOptionType,
                  optionB: BaseOptionType
                ) =>
                  (optionA?.label?.toString() ?? '')
                    .toLowerCase()
                    .localeCompare(
                      (optionB?.label?.toString() ?? '').toLowerCase()
                    )
                }
                onChange={field.onChange}
                value={selectValue}
                options={(transportTypes || [])
                  .filter((type) => type && (type.label || type.value))
                  .map((type) => ({
                    value: type.value || type.label || '',
                    label: type.label || type.value || '',
                  }))}
              />
            );
          }}
        />
        <ErrorMessage
          errors={errors}
          name={'specifications.transportType'}
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>

      {/* Pallets and Pieces */}
      <Grid cols='2' gap='sm' className='mx-0 w-full col-span-4'>
        <div className='col-span-1'>
          <LoadBuildingTextInput
            name='specifications.totalOutPalletCount'
            label='Pallets'
            inputType='number'
            options={{
              valueAsNumber: true,
              min: { value: 0, message: 'Pallets cannot be negative' },
            }}
          />
        </div>
        <div className='col-span-1'>
          <LoadBuildingTextInput
            name='specifications.totalPieces.val'
            label='Pieces'
            inputType='number'
            options={{ valueAsNumber: true }}
          />
        </div>
      </Grid>

      {isRefrigerated && (
        <>
          <div className='col-span-4'>
            <Label name='temperature' required={true}>
              Temperature
            </Label>
            <div className='relative flex items-center'>
              <Controller
                name='specifications.minTempFahrenheit'
                control={control}
                rules={{
                  required:
                    'Temperature is required for refrigerated equipment',
                }}
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<
                    NormalizedLoad,
                    'specifications.minTempFahrenheit'
                  >;
                }) => (
                  <Flex
                    direction='row'
                    gap='sm'
                    className='items-center w-full'
                  >
                    <Input
                      {...field}
                      disabled={false}
                      placeholder='e.g. 32'
                      value={tempDisplayValue}
                      onChange={(e) => setTempDisplayValue(e.target.value)}
                      onFocus={() => setIsTempInputFocused(true)}
                      onBlur={() => {
                        setIsTempInputFocused(false);
                        const fahrenheit =
                          parseInputToFahrenheit(tempDisplayValue);
                        field.onChange(fahrenheit);
                      }}
                    />
                    <TemperatureUnitSelector
                      value={tempUnit}
                      onChange={(newUnit) => setTempUnit(newUnit)}
                    />
                  </Flex>
                )}
              />
            </div>
            <ErrorMessage
              errors={errors}
              name={'specifications.minTempFahrenheit'}
              render={({ message }: { message: string }) => (
                <p className='text-error-500 text-xs'>{message}</p>
              )}
            />
          </div>
        </>
      )}

      {/* TODO: Uncomment when commodities are confirmed to work end-to-end for 3G TMS
          Currently commented out as this feature may not be scoped to this ticket.
          Commodities are included in the form submission payload, but need to verify
          the 3G API properly handles and stores commodity data before enabling this UI.
      */}
      {/* <CommoditiesFieldArray formMethods={formMethods} /> */}
    </Grid>
  );
}

// TODO: Uncomment when commodities are confirmed to work end-to-end for 3G TMS
// Also need to re-add these imports when uncommenting:
// - useFieldArray from 'react-hook-form'
// - AnimatePresence from 'framer-motion'
// - AddActionButton from 'components/AddActionButton'
// - CommodityItemCard from 'pages/QuoteView/LoadBuilding/TurvoSectionForms/components/CommodityItemCard'
// - createNewCommodity from 'types/Load'
// - cn from 'utils/shadcn'
//
// function CommoditiesFieldArray({
//   formMethods,
// }: {
//   formMethods: UseFormReturn<NormalizedLoad>;
// }) {
//   const { control } = formMethods;
//
//   const { fields, append, remove } = useFieldArray({
//     control,
//     name: 'commodities',
//   });
//
//   const addCommodity = () => append(createNewCommodity());
//
//   return (
//     <div className='col-span-4'>
//       <AddActionButton
//         onClick={addCommodity}
//         buttonText='Add Item'
//         descriptionText='Add item(s) for the shipment'
//         buttonNamePosthog='add_commodity_item'
//         className='mb-2'
//       />
//
//       <AnimatePresence>
//         <div className={cn(fields.length > 0 && 'space-y-2')}>
//           {fields.map((field, index) => (
//             <CommodityItemCard
//               key={field.id}
//               index={index}
//               formMethods={formMethods}
//               onRemove={remove}
//             />
//           ))}
//         </div>
//       </AnimatePresence>
//     </div>
//   );
// }
